import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, User, Loader2, ArrowLeft } from "lucide-react";
import { api, setToken } from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("Enter your username and password.");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${api}/auth/login`, { username, password });
      setToken(r.data.token);
      toast.success(`Welcome back, ${r.data.user.name}.`);
      navigate("/admin", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink text-white flex items-center justify-center px-6 relative overflow-hidden">
      {/* Subtle background grid */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
           style={{ backgroundImage: "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)", backgroundSize: "50px 50px" }} />

      <button onClick={() => navigate("/")} data-testid="login-back-home"
              className="absolute top-6 left-6 flex items-center gap-2 text-zinc-400 hover:text-white font-mono text-xs uppercase tracking-[0.25em]">
        <ArrowLeft className="w-4 h-4" /> Back to site
      </button>

      <div className="w-full max-w-md relative">
        <div className="flex justify-center mb-10">
          <img src="/brand/gi-logo.jpg" alt="GI Detailing" style={{ mixBlendMode: 'lighten' }} className="h-20 w-auto" />
        </div>

        <div className="border border-white/10 bg-surface/70 backdrop-blur-md p-8 md:p-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-zinc-500 mb-3">— Control Room</div>
          <h1 className="font-serif text-3xl md:text-4xl mb-2">Admin login<span className="text-zinc-600">.</span></h1>
          <p className="text-zinc-400 text-sm font-light mb-8">Authorized access only.</p>

          <form onSubmit={submit} className="space-y-5" data-testid="login-form">
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 block">Username</span>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  data-testid="login-username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full bg-ink border border-white/10 focus:border-white/50 outline-none pl-10 pr-3 py-3 text-sm font-sans transition-colors"
                  placeholder="Your username"
                />
              </div>
            </label>
            <label className="block">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2 block">Password</span>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  data-testid="login-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-ink border border-white/10 focus:border-white/50 outline-none pl-10 pr-3 py-3 text-sm font-sans transition-colors"
                  placeholder="••••••••"
                />
              </div>
            </label>
            <button
              data-testid="login-submit"
              disabled={loading}
              className="beam-btn w-full bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] py-3.5 hover:bg-zinc-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Signing in..." : "Sign in"}
            </button>
          </form>
        </div>

        <div className="text-center mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-600">
          GI Detailing · Admin
        </div>
      </div>
    </div>
  );
}
